/**
 * Profile Achievements "Stats" sub-view: snippet counts / mastery and track mastery counts,
 * scoped by language (or all cookbook decks when language is empty).
 *
 * Preferred path: read denormalized `users/{uid}/profile_stats_cache/achievement_summary` (maintained by Cloud Functions).
 * Scope rows include `snippetsMastered` (count) and `masteredSnippets` (full list of mastered cards).
 */

import { collection, doc, documentId, getDoc, getDocs, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app, { db } from '../firebase/firebaseConfig';
import { safeGetDoc } from '../utils/firestoreSafeGet';
import { normalizeLanguageKey, fetchTrackMetaBatch } from './achievementsService';
import { getRoadmapTrackIdFromUserDeckData } from '../utils/roadmapSubscription';
import { getCookbookForNode, isRoadmapUserCookbook, loadTrackNodes } from './roadmapService';
import SpacedRepetition from '../components/SRS/SpacedRepetition';
import { MAX_SNIPPET_LEVEL } from '../components/SRS/LevelingSystem';
import { isCardMastered } from '../components/SRS/algorithm';
import {
  formatCookbookIndexReviewLabel,
  isCookbookSnippetDueToday,
  isCookbookSnippetStarted
} from '../utils/studySessionDotState';
import { resolveSrsNextReviewDate } from '../components/SRS/algorithm';
import { fetchUserReviewSnapshot } from './reviewQueueService';

/**
 * EXP target for the card’s level-up bar (same formula as LevelingSystem.calculateExpToLevel(nextLevel)).
 * @param {number} currentLevel
 */
function cookbookSnippetExpToNextLevel(currentLevel) {
  const L = Math.min(MAX_SNIPPET_LEVEL, Math.max(1, Math.round(currentLevel)));
  const targetLevel = Math.min(MAX_SNIPPET_LEVEL + 1, L + 1);
  return Math.round(100 * Math.pow(10, (targetLevel - 1) / (99 / Math.log(10))));
}

/** Tags used only for bookkeeping; hidden from learner-facing snippet tag filters. */
const SNIPPET_FILTER_EXCLUDED_TAG_LOWER = new Set(['roadmap', 'cookbook']);

/**
 * @param {unknown} tags
 * @returns {string[]}
 */
function normalizeStringTagList(tags) {
  if (!Array.isArray(tags)) return [];
  const out = [];
  for (const t of tags) {
    if (typeof t !== 'string') continue;
    const s = t.trim();
    if (s) out.push(s);
  }
  return out;
}

/**
 * Combined deck + card tags for filtering, minus structural duplicates of the track id.
 * @param {string[]} deckTags
 * @param {string[]} cardTags
 * @param {string|null} trackId
 * @returns {string[]}
 */
export function buildSnippetFilterTags(deckTags, cardTags, trackId) {
  const raw = [...normalizeStringTagList(deckTags), ...normalizeStringTagList(cardTags)];
  const out = [];
  const seenLower = new Set();
  for (const t of raw) {
    const lower = t.toLowerCase();
    if (SNIPPET_FILTER_EXCLUDED_TAG_LOWER.has(lower)) continue;
    if (trackId && t === trackId) continue;
    if (seenLower.has(lower)) continue;
    seenLower.add(lower);
    out.push(t);
  }
  return out;
}

/**
 * Firestore ref for cached achievement stats (scopes.all = all languages).
 * Uses path segments so an invalid/empty userId cannot produce a broken path (empty path segment → SDK errors).
 */
export function achievementStatsCacheDocRef(userId) {
  const uid = typeof userId === 'string' ? userId.trim() : '';
  if (!uid) {
    throw new Error('achievementStatsCacheDocRef: userId is required');
  }
  return doc(db, 'users', uid, 'profile_stats_cache', 'achievement_summary');
}

/**
 * Maps UI language filter (empty = all) to Firestore scope key.
 * @param {string} [languageFilter]
 * @returns {'all' | string}
 */
export function achievementStatsScopeKey(languageFilter) {
  return languageFilter ? normalizeLanguageKey(languageFilter) : 'all';
}

/**
 * Calls Cloud Function to recompute and write profile_stats_cache (us-central1).
 */
export async function refreshAchievementStatsCacheCallable() {
  const functions = getFunctions(app, 'us-central1');
  const refresh = httpsCallable(functions, 'refreshProfileAchievementStatsCache');
  await refresh();
}

/**
 * All cookbook cards for the user, optionally filtered by deck language.
 * @returns {{ totalSnippets: number, avgMastery: number }}
 */
export async function loadCookbookSnippetAggregates(userId, languageKeyOrEmpty) {
  if (!userId) {
    return { totalSnippets: 0, avgMastery: 0 };
  }

  const decksSnap = await getDocs(collection(db, `users/${userId}/decks`));
  const filterLang = languageKeyOrEmpty ? normalizeLanguageKey(languageKeyOrEmpty) : null;

  let totalSnippets = 0;
  let masterySum = 0;

  for (const deckDoc of decksSnap.docs) {
    const d = deckDoc.data();
    const isCookbook = d.type === 'cookbook' || !d.type;
    if (!isCookbook) continue;

    if (filterLang) {
      const deckLang = normalizeLanguageKey(d.language);
      if (deckLang !== filterLang) continue;
    }

    const cardsSnap = await getDocs(collection(db, `users/${userId}/decks/${deckDoc.id}/cards`));
    for (const cardDoc of cardsSnap.docs) {
      totalSnippets += 1;
      const progRef = doc(db, `users/${userId}/progress/${deckDoc.id}_${cardDoc.id}`);
      const progSnap = await getDoc(progRef);
      const m = progSnap.exists() ? progSnap.data().mastery || 0 : 0;
      masterySum += m;
    }
  }

  const avgMastery = totalSnippets > 0 ? Math.round(masterySum / totalSnippets) : 0;
  return { totalSnippets, avgMastery };
}

/**
 * SRS success rate from progress doc (0–100), or 0 if not enough reviews.
 * @param {Record<string, unknown> | null | undefined} p
 * @returns {number}
 */
function srsSuccessRateFromProgress(p) {
  if (!p || typeof p !== 'object') return 0;
  const tr = Number(p.totalReviews);
  const ok = Number(p.successfulReviews);
  const total = Number.isFinite(tr) && tr > 0 ? tr : 0;
  const good = Number.isFinite(ok) && ok >= 0 ? ok : 0;
  return total > 0 ? Math.round((good / total) * 100) : 0;
}

/**
 * Whether this progress doc counts as "snippet mastered". Always recomputes
 * from the recency-weighted mastery score via {@link isCardMastered} so the
 * answer stays consistent with the current rule even when the denormalized
 * `is_mastered` flag on the doc is stale (e.g. written under an older rule
 * before the next review re-stamps it).
 * @param {Record<string, unknown> | null | undefined} p
 */
export function snippetMasteredFromProgress(p) {
  return isCardMastered(p);
}

/** Firestore `in` limit for documentId() queries. */
const PROGRESS_BATCH_IN_LIMIT = 30;

/**
 * Batch-load progress docs by id (users/{uid}/progress/{deckId}_{cardId}).
 * Replaces one getDoc per card with ceil(n/30) queries, chunks run in parallel.
 * @param {string} userId
 * @param {string[]} progressDocIds
 * @returns {Promise<Map<string, Record<string, unknown>>>}
 */
async function fetchUserProgressDocsById(userId, progressDocIds) {
  /** @type {Map<string, Record<string, unknown>>} */
  const map = new Map();
  const uid = typeof userId === 'string' ? userId.trim() : '';
  if (!uid || !progressDocIds.length) return map;

  const unique = [...new Set(progressDocIds.filter((id) => typeof id === 'string' && id))];
  const chunks = [];
  for (let i = 0; i < unique.length; i += PROGRESS_BATCH_IN_LIMIT) {
    chunks.push(unique.slice(i, i + PROGRESS_BATCH_IN_LIMIT));
  }

  try {
    await Promise.all(
      chunks.map(async (ids) => {
        if (!ids.length) return;
        const qRef = query(
          collection(db, `users/${uid}/progress`),
          where(documentId(), 'in', ids)
        );
        const snap = await getDocs(qRef);
        snap.forEach((d) => {
          map.set(d.id, d.data());
        });
      })
    );
  } catch (e) {
    console.warn('fetchUserProgressDocsById: batch read failed', e?.code, e?.message);
  }

  return map;
}

/**
 * SRS stats keyed by Firestore roadmap `snippetId`, from the user's cookbook cards + progress.
 * Shape matches {@link computeTrackSnippetMastery} in RoadmapList (`successRate`, review counts).
 *
 * @param {string} userId
 * @param {string[]} snippetIds
 * @param {{ withDueSnippetIds?: boolean }} [options]
 * @returns {Promise<Record<string, { successRate: number, totalReviews: number, totalAttempts: number }> | { statsBySnippetId: Record<string, { successRate: number, totalReviews: number, totalAttempts: number }>, dueSnippetIds: Set<string> }>}
 */
export async function loadRoadmapSnippetStatsByIds(userId, snippetIds, options = {}) {
  const { withDueSnippetIds = false } = options;
  const uid = typeof userId === 'string' ? userId.trim() : '';
  const targetSet = new Set((snippetIds || []).filter((id) => typeof id === 'string' && id.trim()));
  if (!uid || targetSet.size === 0) {
    return withDueSnippetIds
      ? { statsBySnippetId: {}, dueSnippetIds: new Set() }
      : {};
  }

  const srs = new SpacedRepetition(uid);
  const [decksSnap, progressRows] = await fetchUserReviewSnapshot(uid, srs);

  const progressMap = new Map();
  for (const row of progressRows) {
    if (row.deckId && row.cardId) {
      progressMap.set(`${row.deckId}_${row.cardId}`, row);
    } else if (typeof row.id === 'string' && row.id.includes('_')) {
      // Legacy / partial writes: doc id is `${deckId}_${cardId}`.
      progressMap.set(row.id, row);
    }
  }

  /** @type {Record<string, { successRate: number, totalReviews: number, totalAttempts: number }>} */
  const statsBySnippetId = {};
  const dueSnippetIds = new Set();
  const now = new Date();

  const roadmapDeckDocs = decksSnap.docs.filter((deckDoc) => {
    const deckData = deckDoc.data();
    const isCookbook = deckData.type === 'cookbook' || !deckData.type;
    return isCookbook && isRoadmapUserCookbook({ id: deckDoc.id, ...deckData });
  });

  const cardsByDeck = await Promise.all(
    roadmapDeckDocs.map(async (deckDoc) => {
      const snap = await getDocs(collection(db, `users/${uid}/decks/${deckDoc.id}/cards`));
      return { deckId: deckDoc.id, cardsSnap: snap };
    })
  );

  for (const { deckId, cardsSnap } of cardsByDeck) {
    for (const cardDoc of cardsSnap.docs) {
      const cardData = cardDoc.data();
      const snippetId =
        cardData.roadmapSnippetId != null ? String(cardData.roadmapSnippetId).trim() : '';
      if (!snippetId || !targetSet.has(snippetId)) continue;

      const p = progressMap.get(`${deckId}_${cardDoc.id}`) ?? null;
      const successRate = srsSuccessRateFromProgress(p);
      const totalReviews = p && Number.isFinite(Number(p.totalReviews)) ? Number(p.totalReviews) : 0;
      const rawLevel = cardData?.level;
      const level =
        typeof rawLevel === 'number' && Number.isFinite(rawLevel)
          ? Math.min(Math.max(Math.round(rawLevel), 1), MAX_SNIPPET_LEVEL)
          : 1;
      const rawExp = cardData?.current_exp;
      const current_exp =
        typeof rawExp === 'number' && Number.isFinite(rawExp) ? Math.max(0, Math.round(rawExp)) : 0;
      const rawExpTo = cardData?.exp_to_level;
      const exp_to_level =
        typeof rawExpTo === 'number' && Number.isFinite(rawExpTo) && rawExpTo > 0
          ? Math.round(rawExpTo)
          : cookbookSnippetExpToNextLevel(level);
      const isMastered = snippetMasteredFromProgress(p);
      const mastery = p ? Math.round(Math.min(100, Math.max(0, Number(p.mastery) || 0))) : 0;
      const repetitions = p && Number.isFinite(Number(p.repetitions)) ? Number(p.repetitions) : 0;
      const interval = p && Number.isFinite(Number(p.interval)) ? Number(p.interval) : 0;
      const firstReviewed =
        p && typeof p.firstReviewed === 'string' ? p.firstReviewed : null;
      const lastReviewed =
        p && typeof p.lastReviewed === 'string' ? p.lastReviewed : null;

      const candidate = {
        successRate,
        totalReviews,
        totalAttempts: totalReviews,
        level,
        current_exp,
        exp_to_level,
        isMastered,
        mastery,
        repetitions,
        interval,
        firstReviewed,
        lastReviewed
      };

      const existing = statsBySnippetId[snippetId];
      const shouldReplace =
        !existing ||
        level > (existing.level || 1) ||
        (isMastered && !existing.isMastered) ||
        (level === (existing.level || 1) && totalReviews > (existing.totalReviews || 0));
      if (shouldReplace) {
        statsBySnippetId[snippetId] = candidate;
      }

      // Match Study Mode yellow dots: studied and due today. Missing progress is
      // "not started" (grey), not due — isSrsProgressRowDue(null) would treat it as due.
      if (withDueSnippetIds && isCookbookSnippetDueToday(p, now)) {
        dueSnippetIds.add(snippetId);
      }
    }
  }

  if (withDueSnippetIds) {
    return { statsBySnippetId, dueSnippetIds };
  }
  return statsBySnippetId;
}

/**
 * Profile Skills: summary aggregates plus each cookbook card (level capped at {@link MAX_SNIPPET_LEVEL},
 * mastery and SRS success from progress). Optimized: parallel card collection reads + batched progress queries.
 * @returns {Promise<{
 *   totalSnippets: number,
 *   avgMastery: number,
 *   avgSnippetLevel: number,
 *   snippetsMastered: number,
 *   snippets: Array<{
 *     deckId: string,
 *     cardId: string,
 *     snippetTitle: string,
 *     deckName: string,
 *     language: string,
 *     level: number,
 *     current_exp: number,
 *     exp_to_level: number,
 *     mastery: number,
 *     successRate: number,
 *     is_mastered: boolean,
 *     trackId: string | null,
 *     trackTitle: string | null,
 *     snippetTags: string[]
 *   }>
 * }>}
 */
export async function loadCookbookSnippetSkillsPageData(userId, languageKeyOrEmpty) {
  const empty = {
    totalSnippets: 0,
    avgMastery: 0,
    avgSnippetLevel: 0,
    snippetsMastered: 0,
    snippets: []
  };
  if (!userId) return empty;

  let decksSnap;
  try {
    decksSnap = await getDocs(collection(db, `users/${userId}/decks`));
  } catch (e) {
    console.warn('loadCookbookSnippetSkillsPageData: decks query failed', e?.code, e?.message);
    return empty;
  }

  const filterLang = languageKeyOrEmpty ? normalizeLanguageKey(languageKeyOrEmpty) : null;

  /** @type {Array<{ deckId: string, deckName: string, langRaw: string, trackId: string | null, deckTags: unknown[] }>} */
  const cookbooks = [];
  for (const deckDoc of decksSnap.docs) {
    if (!deckDoc.id) continue;
    const d = deckDoc.data();
    const isCookbook = d.type === 'cookbook' || !d.type;
    if (!isCookbook) continue;

    if (filterLang) {
      const deckLang = normalizeLanguageKey(d.language);
      if (deckLang !== filterLang) continue;
    }

    const deckName = typeof d.name === 'string' && d.name.trim() ? d.name.trim() : 'Cookbook';
    const langRaw = typeof d.language === 'string' && d.language.trim() ? d.language.trim() : 'other';
    const trackIdRaw = getRoadmapTrackIdFromUserDeckData(d);
    const trackId = trackIdRaw && String(trackIdRaw).trim() ? String(trackIdRaw).trim() : null;
    const deckTags = Array.isArray(d.tags) ? d.tags : [];
    cookbooks.push({ deckId: deckDoc.id, deckName, langRaw, trackId, deckTags });
  }

  const deckCardResults = await Promise.all(
    cookbooks.map(async (cb) => {
      try {
        const cardsSnap = await getDocs(collection(db, `users/${userId}/decks/${cb.deckId}/cards`));
        return { ...cb, cardDocs: cardsSnap.docs };
      } catch (e) {
        console.warn('loadCookbookSnippetSkillsPageData: cards query failed', cb.deckId, e?.code);
        return { ...cb, cardDocs: [] };
      }
    })
  );

  const progressIds = [];
  for (const cr of deckCardResults) {
    for (const cardDoc of cr.cardDocs) {
      if (cardDoc.id) progressIds.push(`${cr.deckId}_${cardDoc.id}`);
    }
  }

  const progressMap = await fetchUserProgressDocsById(userId, progressIds);

  let totalSnippets = 0;
  let masterySum = 0;
  let levelSum = 0;
  let snippetsMastered = 0;
  /** @type {typeof empty.snippets} */
  const snippets = [];

  for (const cr of deckCardResults) {
    const { deckId, deckName, langRaw, cardDocs, trackId, deckTags } = cr;
    for (const cardDoc of cardDocs) {
      totalSnippets += 1;
      const cardData = cardDoc.data();
      const rawLevel = cardData?.level;
      const lvl =
        typeof rawLevel === 'number' && Number.isFinite(rawLevel)
          ? Math.min(Math.max(Math.round(rawLevel), 1), MAX_SNIPPET_LEVEL)
          : 1;
      levelSum += lvl;

      const p = progressMap.get(`${deckId}_${cardDoc.id}`) ?? null;
      /** @type {Record<string, unknown> | null} */
      const pData = p && typeof p === 'object' ? p : null;
      const m = pData ? Number(pData.mastery) || 0 : 0;
      masterySum += m;
      if (snippetMasteredFromProgress(pData)) snippetsMastered += 1;

      const snippetTitle =
        typeof cardData?.title === 'string' && cardData.title.trim()
          ? cardData.title.trim()
          : 'Untitled snippet';

      const rawExp = cardData?.current_exp;
      const currentExp =
        typeof rawExp === 'number' && Number.isFinite(rawExp) ? Math.max(0, Math.round(rawExp)) : 0;
      const rawExpTo = cardData?.exp_to_level;
      const expToLevel =
        typeof rawExpTo === 'number' && Number.isFinite(rawExpTo) && rawExpTo > 0
          ? Math.round(rawExpTo)
          : cookbookSnippetExpToNextLevel(lvl);

      const snippetTags = buildSnippetFilterTags(deckTags, cardData?.tags, trackId);

      const isStarted = isCookbookSnippetStarted(pData);
      const isDueToday = isCookbookSnippetDueToday(pData);
      const reviewLabel = formatCookbookIndexReviewLabel(pData);
      const dueDate = isStarted ? resolveSrsNextReviewDate(pData) : null;
      const nextReviewMs =
        dueDate && !Number.isNaN(dueDate.getTime()) ? dueDate.getTime() : null;

      snippets.push({
        deckId,
        cardId: cardDoc.id,
        snippetTitle,
        deckName,
        language: langRaw,
        level: lvl,
        current_exp: currentExp,
        exp_to_level: expToLevel,
        mastery: Math.round(Math.min(100, Math.max(0, m))),
        successRate: srsSuccessRateFromProgress(pData),
        is_mastered: snippetMasteredFromProgress(pData),
        is_started: isStarted,
        is_due_today: isDueToday,
        reviewLabel,
        nextReviewMs,
        trackId,
        trackTitle: null,
        snippetTags
      });
    }
  }

  const distinctTrackIds = [...new Set(snippets.map((s) => s.trackId).filter((tid) => tid))];
  if (distinctTrackIds.length > 0) {
    const titleById = await fetchTrackMetaBatch(distinctTrackIds);
    for (const s of snippets) {
      if (!s.trackId) continue;
      const meta = titleById.get(s.trackId);
      s.trackTitle = (meta?.title && String(meta.title).trim()) || s.trackId;
    }
  }

  snippets.sort((a, b) => {
    const rank = (s) => {
      if (!s.is_started) return 2;
      if (s.is_due_today) return 0;
      return 1;
    };
    const rankDiff = rank(a) - rank(b);
    if (rankDiff !== 0) return rankDiff;
    if (a.nextReviewMs != null && b.nextReviewMs != null && a.nextReviewMs !== b.nextReviewMs) {
      return a.nextReviewMs - b.nextReviewMs;
    }
    if (a.nextReviewMs != null && b.nextReviewMs == null) return -1;
    if (a.nextReviewMs == null && b.nextReviewMs != null) return 1;
    return a.snippetTitle.localeCompare(b.snippetTitle, undefined, { sensitivity: 'base' });
  });

  const avgMastery = totalSnippets > 0 ? Math.round(masterySum / totalSnippets) : 0;
  const avgSnippetLevel =
    totalSnippets > 0 ? Math.round((levelSum / totalSnippets) * 10) / 10 : 0;
  return { totalSnippets, avgMastery, avgSnippetLevel, snippetsMastered, snippets };
}

/**
 * Summary fields only; delegates to {@link loadCookbookSnippetSkillsPageData}.
 * Kept so older dev-server bundles or imports that still call this name do not break.
 * @returns {Promise<{ totalSnippets: number, avgMastery: number, avgSnippetLevel: number, snippetsMastered: number }>}
 */
export async function loadCookbookSnippetSkillsAggregates(userId, languageKeyOrEmpty) {
  const d = await loadCookbookSnippetSkillsPageData(userId, languageKeyOrEmpty);
  return {
    totalSnippets: d.totalSnippets,
    avgMastery: d.avgMastery,
    avgSnippetLevel: d.avgSnippetLevel,
    snippetsMastered: d.snippetsMastered
  };
}

export async function countSnippetCardsMasteredBySrs(userId, languageKeyOrEmpty) {
  if (!userId) return 0;

  let decksSnap;
  try {
    decksSnap = await getDocs(collection(db, `users/${userId}/decks`));
  } catch (e) {
    console.warn('countSnippetCardsMasteredBySrs: decks query failed', e?.code, e?.message);
    return 0;
  }

  const filterLang = languageKeyOrEmpty ? normalizeLanguageKey(languageKeyOrEmpty) : null;
  let mastered = 0;

  for (const deckDoc of decksSnap.docs) {
    if (!deckDoc.id) continue;
    const d = deckDoc.data();
    const isCookbook = d.type === 'cookbook' || !d.type;
    if (!isCookbook) continue;

    if (filterLang) {
      const deckLang = normalizeLanguageKey(d.language);
      if (deckLang !== filterLang) continue;
    }

    let cardsSnap;
    try {
      cardsSnap = await getDocs(collection(db, `users/${userId}/decks/${deckDoc.id}/cards`));
    } catch (e) {
      console.warn('countSnippetCardsMasteredBySrs: cards query failed', deckDoc.id, e?.code);
      continue;
    }

    let queryOk = false;
    /** @type {Set<string> | null} */
    let masteredCardIdsFromQuery = null;
    try {
      const qSnap = await getDocs(
        query(
          collection(db, `users/${userId}/progress`),
          where('deckId', '==', deckDoc.id),
          where('is_mastered', '==', true)
        )
      );
      queryOk = true;
      masteredCardIdsFromQuery = new Set();
      qSnap.forEach((d) => {
        const p = d.data();
        // Re-verify against the current mastered rule; the denormalized
        // `is_mastered` flag may have been written under an older rule.
        if (!isCardMastered(p)) return;
        const c = p?.cardId;
        if (typeof c === 'string' && c) masteredCardIdsFromQuery.add(c);
      });
      mastered += masteredCardIdsFromQuery.size;
    } catch (e) {
      console.warn('countSnippetCardsMasteredBySrs: is_mastered query failed', deckDoc.id, e?.code);
    }

    for (const cardDoc of cardsSnap.docs) {
      if (!cardDoc.id) continue;
      try {
        if (queryOk && masteredCardIdsFromQuery?.has(cardDoc.id)) continue;

        const progRef = doc(db, `users/${userId}/progress/${deckDoc.id}_${cardDoc.id}`);
        const progSnap = await getDoc(progRef);
        if (!progSnap.exists()) continue;
        const p = progSnap.data();
        // Always recompute: a stale `is_mastered=false` may not reflect the
        // current rule (recency-weighted mastery score).
        if (snippetMasteredFromProgress(p)) mastered += 1;
      } catch (e) {
        console.warn(
          'countSnippetCardsMasteredBySrs: progress read skipped',
          `${deckDoc.id}_${cardDoc.id}`,
          e?.code
        );
      }
    }
  }

  return mastered;
}

/**
 * Cookbook cards counted as mastered by {@link snippetMasteredFromProgress} (same rule as {@link countSnippetCardsMasteredBySrs}).
 * Sorted by snippet title, then cookbook name.
 * @param {string} languageKeyOrEmpty
 * @returns {Promise<Array<{ deckId: string, cardId: string, snippetTitle: string, deckName: string, language: string, successRate: number, is_mastered: boolean }>>}
 */
export async function listMasteredCookbookSnippetCards(userId, languageKeyOrEmpty) {
  if (!userId) return [];

  let decksSnap;
  try {
    decksSnap = await getDocs(collection(db, `users/${userId}/decks`));
  } catch (e) {
    console.warn('listMasteredCookbookSnippetCards: decks query failed', e?.code, e?.message);
    return [];
  }

  const filterLang = languageKeyOrEmpty ? normalizeLanguageKey(languageKeyOrEmpty) : null;
  /** @type {Array<{ deckId: string, cardId: string, snippetTitle: string, deckName: string, language: string, successRate: number, is_mastered: boolean }>} */
  const out = [];

  for (const deckDoc of decksSnap.docs) {
    if (!deckDoc.id) continue;
    const d = deckDoc.data();
    const isCookbook = d.type === 'cookbook' || !d.type;
    if (!isCookbook) continue;

    if (filterLang) {
      const deckLang = normalizeLanguageKey(d.language);
      if (deckLang !== filterLang) continue;
    }

    const deckName = typeof d.name === 'string' && d.name.trim() ? d.name.trim() : 'Cookbook';
    const langRaw = typeof d.language === 'string' && d.language.trim() ? d.language.trim() : 'other';

    let cardsSnap;
    try {
      cardsSnap = await getDocs(collection(db, `users/${userId}/decks/${deckDoc.id}/cards`));
    } catch (e) {
      console.warn('listMasteredCookbookSnippetCards: cards query failed', deckDoc.id, e?.code);
      continue;
    }

    const pushIfMastered = (cardId, progData, cardData) => {
      if (!snippetMasteredFromProgress(progData)) return;
      const sr = srsSuccessRateFromProgress(progData);
      const cd = cardData || {};
      const snippetTitle =
        typeof cd.title === 'string' && cd.title.trim() ? cd.title.trim() : 'Untitled snippet';
      out.push({
        deckId: deckDoc.id,
        cardId,
        snippetTitle,
        deckName,
        language: langRaw,
        successRate: sr,
        is_mastered: true
      });
    };

    let queryOk = false;
    /** @type {Set<string> | null} */
    let seenFromQuery = null;
    try {
      const qSnap = await getDocs(
        query(
          collection(db, `users/${userId}/progress`),
          where('deckId', '==', deckDoc.id),
          where('is_mastered', '==', true)
        )
      );
      queryOk = true;
      seenFromQuery = new Set();
      for (const progDoc of qSnap.docs) {
        const p = progDoc.data();
        const cardId = typeof p.cardId === 'string' ? p.cardId : '';
        if (!cardId || seenFromQuery.has(cardId)) continue;
        seenFromQuery.add(cardId);
        // Re-verify against the current mastered rule before pushing; the
        // denormalized `is_mastered` flag may have been written under an
        // older rule. pushIfMastered will also re-check via the helper.
        if (!isCardMastered(p)) continue;
        const cardSnap = await getDoc(
          doc(db, `users/${userId}/decks/${deckDoc.id}/cards/${cardId}`)
        );
        const cd = cardSnap.exists() ? cardSnap.data() : {};
        pushIfMastered(cardId, p, cd);
      }
    } catch (e) {
      console.warn('listMasteredCookbookSnippetCards: is_mastered query failed', deckDoc.id, e?.code);
    }

    for (const cardDoc of cardsSnap.docs) {
      if (!cardDoc.id) continue;
      if (queryOk && seenFromQuery?.has(cardDoc.id)) continue;
      try {
        const progRef = doc(db, `users/${userId}/progress/${deckDoc.id}_${cardDoc.id}`);
        const progSnap = await getDoc(progRef);
        if (!progSnap.exists()) continue;
        const p = progSnap.data();
        // Always recompute: a stale `is_mastered=false` may not reflect the
        // current rule (recency-weighted mastery score).
        pushIfMastered(cardDoc.id, p, cardDoc.data());
      } catch (e) {
        console.warn(
          'listMasteredCookbookSnippetCards: progress read skipped',
          `${deckDoc.id}_${cardDoc.id}`,
          e?.code
        );
      }
    }
  }

  out.sort((a, b) => {
    const t = a.snippetTitle.localeCompare(b.snippetTitle, undefined, { sensitivity: 'base' });
    if (t !== 0) return t;
    return a.deckName.localeCompare(b.deckName, undefined, { sensitivity: 'base' });
  });

  return out;
}

/**
 * @param {Record<string, unknown> | null | undefined} row — scope row from profile_stats_cache
 * @returns {number | null} — null if not present / invalid
 */
export function readSnippetsMasteredFromScopeRow(row) {
  if (!row || row.snippetsMastered === undefined || row.snippetsMastered === null) return null;
  const n = Number(row.snippetsMastered);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {Record<string, unknown> | null | undefined} row — scope row from profile_stats_cache
 * @returns {Array<{ deckId: string, cardId: string, snippetTitle: string, deckName: string, language: string, successRate: number }> | null}
 *         `null` when cache predates masteredSnippets; empty array when genuinely none mastered.
 */
export function readMasteredSnippetsFromScopeRow(row) {
  if (!row || row.masteredSnippets === undefined || row.masteredSnippets === null) return null;
  if (!Array.isArray(row.masteredSnippets)) return null;
  const out = [];
  for (const it of row.masteredSnippets) {
    if (!it || typeof it !== 'object') return null;
    const deckId = typeof it.deckId === 'string' ? it.deckId : '';
    const cardId = typeof it.cardId === 'string' ? it.cardId : '';
    if (!deckId || !cardId) return null;
    const snippetTitle =
      typeof it.snippetTitle === 'string' && it.snippetTitle.trim()
        ? it.snippetTitle.trim()
        : 'Untitled snippet';
    const deckName =
      typeof it.deckName === 'string' && it.deckName.trim() ? it.deckName.trim() : 'Cookbook';
    const language = typeof it.language === 'string' && it.language.trim() ? it.language.trim() : 'other';
    const sr = Number(it.successRate);
    if (!Number.isFinite(sr)) return null;
    const is_mastered = typeof it.is_mastered === 'boolean' ? it.is_mastered : true;
    out.push({ deckId, cardId, snippetTitle, deckName, language, successRate: sr, is_mastered });
  }
  return out;
}

/**
 * Reads mastered snippet list from profile_stats_cache when present; otherwise computes client-side.
 * @returns {Promise<{ items: Array<{ deckId: string, cardId: string, snippetTitle: string, deckName: string, language: string, successRate: number }>, needsCacheWarmup: boolean }>}
 */
export async function loadMasteredSnippetsFromCacheOrLegacy(userId, languageFilter) {
  const uid = typeof userId === 'string' ? userId.trim() : '';
  if (!uid) return { items: [], needsCacheWarmup: false };

  const scopeKey = achievementStatsScopeKey(languageFilter);
  let cacheRef;
  try {
    cacheRef = achievementStatsCacheDocRef(uid);
  } catch (e) {
    console.warn('loadMasteredSnippetsFromCacheOrLegacy: invalid cache ref', e);
    try {
      const items = await listMasteredCookbookSnippetCards(uid, languageFilter || '');
      return { items, needsCacheWarmup: true };
    } catch (inner) {
      console.warn('loadMasteredSnippetsFromCacheOrLegacy: legacy list failed after ref error', inner);
      return { items: [], needsCacheWarmup: true };
    }
  }

  try {
    const snap = await safeGetDoc(cacheRef);
    const row = snap.data()?.scopes?.[scopeKey];
    const cached = readMasteredSnippetsFromScopeRow(row);
    if (cached !== null) {
      return { items: cached, needsCacheWarmup: false };
    }
  } catch (e) {
    console.warn('loadMasteredSnippetsFromCacheOrLegacy: cache read failed', e);
  }

  try {
    const items = await listMasteredCookbookSnippetCards(uid, languageFilter || '');
    return { items, needsCacheWarmup: true };
  } catch (e) {
    console.warn('loadMasteredSnippetsFromCacheOrLegacy: legacy list failed', e?.code, e?.message);
    return { items: [], needsCacheWarmup: true };
  }
}

/**
 * Reads `snippetsMastered` from profile_stats_cache only (1 doc read). No legacy full-library scan.
 * @returns {Promise<number | null>} — null when cache doc/field is absent
 */
export async function loadSnippetsMasteredFromCacheOnly(userId, languageFilter) {
  const uid = typeof userId === 'string' ? userId.trim() : '';
  if (!uid) return null;

  const scopeKey = achievementStatsScopeKey(languageFilter);
  try {
    const snap = await safeGetDoc(achievementStatsCacheDocRef(uid));
    const row = snap.data()?.scopes?.[scopeKey];
    return readSnippetsMasteredFromScopeRow(row);
  } catch (e) {
    console.warn('loadSnippetsMasteredFromCacheOnly: cache read failed', e);
    return null;
  }
}

/**
 * Reads `snippetsMastered` from profile_stats_cache when present; otherwise computes client-side.
 * @returns {Promise<{ count: number, needsCacheWarmup: boolean }>} — `needsCacheWarmup` when cache lacked the field (caller may invoke {@link refreshAchievementStatsCacheCallable} once).
 */
export async function loadSnippetsMasteredFromCacheOrLegacy(userId, languageFilter) {
  const uid = typeof userId === 'string' ? userId.trim() : '';
  if (!uid) return { count: 0, needsCacheWarmup: false };

  const scopeKey = achievementStatsScopeKey(languageFilter);
  let cacheRef;
  try {
    cacheRef = achievementStatsCacheDocRef(uid);
  } catch (e) {
    console.warn('loadSnippetsMasteredFromCacheOrLegacy: invalid cache ref', e);
    try {
      const count = await countSnippetCardsMasteredBySrs(uid, languageFilter || '');
      return { count, needsCacheWarmup: true };
    } catch (inner) {
      console.warn('loadSnippetsMasteredFromCacheOrLegacy: legacy count failed after ref error', inner);
      return { count: 0, needsCacheWarmup: true };
    }
  }

  try {
    const snap = await safeGetDoc(cacheRef);
    const row = snap.data()?.scopes?.[scopeKey];
    const cached = readSnippetsMasteredFromScopeRow(row);
    if (cached !== null) {
      return { count: cached, needsCacheWarmup: false };
    }
  } catch (e) {
    console.warn('loadSnippetsMasteredFromCacheOrLegacy: cache read failed', e);
  }

  try {
    const count = await countSnippetCardsMasteredBySrs(uid, languageFilter || '');
    return { count, needsCacheWarmup: true };
  } catch (e) {
    console.warn('loadSnippetsMasteredFromCacheOrLegacy: legacy count failed', e?.code, e?.message);
    return { count: 0, needsCacheWarmup: true };
  }
}

async function isTrackMasteredByCardSuccessRate(userId, trackId) {
  const nodes = await loadTrackNodes(trackId);
  const required = nodes.filter((n) => Array.isArray(n.snippetIds) && n.snippetIds.length > 0);
  if (required.length === 0) return false;

  for (const node of required) {
    const cookbook = await getCookbookForNode(userId, trackId, node.id);
    if (!cookbook?.id) return false;

    const cardsSnap = await getDocs(collection(db, `users/${userId}/decks/${cookbook.id}/cards`));
    if (cardsSnap.empty) return false;

    for (const cardDoc of cardsSnap.docs) {
      const progRef = doc(db, `users/${userId}/progress/${cookbook.id}_${cardDoc.id}`);
      const progSnap = await getDoc(progRef);
      if (!progSnap.exists()) return false;
      if (!snippetMasteredFromProgress(progSnap.data())) return false;
    }
  }

  return true;
}

/**
 * Count initiated tracks that are fully complete (all tests done) and every cookbook card
 * in those tests has SRS success rate ≥ threshold.
 * @param {string} userId
 * @param {Array<{ trackId: string, isComplete: boolean }>} trackCards — already language-filtered
 */
export async function countMasteredTracks(userId, trackCards) {
  if (!userId || !trackCards?.length) return 0;

  const completed = trackCards.filter((c) => c.isComplete);
  if (completed.length === 0) return 0;

  const results = await Promise.all(
    completed.map((c) => isTrackMasteredByCardSuccessRate(userId, c.trackId))
  );
  return results.filter(Boolean).length;
}
